import { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import {
	Button,
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetDescription
} from '@hiring-intelligence/ui';

interface CrmCreateDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description?: string;
	submitting?: boolean;
	onSubmit: () => void;
	onSubmitAndAddAnother?: () => void;
	submitLabel?: string;
	disabled?: boolean;
	children: ReactNode;
}

/**
 * Right-side slide-over for creating Contacts / Companies / Deals — matches
 * the HubSpot "Create X" drawer (title strip, scrollable body, sticky footer
 * with Create / Create and add another / Cancel).
 *
 * We keep the app's primary color for the title strip rather than HubSpot's
 * teal, per "keep our style".
 */
export function CrmCreateDrawer({
	open,
	onOpenChange,
	title,
	description,
	submitting,
	onSubmit,
	onSubmitAndAddAnother,
	submitLabel = 'Create',
	disabled,
	children
}: CrmCreateDrawerProps) {
	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
				<SheetHeader className="border-b bg-primary/5 px-5 py-3.5">
					<SheetTitle>{title}</SheetTitle>
					{description && <SheetDescription>{description}</SheetDescription>}
				</SheetHeader>
				<div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">{children}</div>
				<div className="border-t bg-background px-5 py-3 flex flex-wrap items-center gap-2">
					<Button onClick={onSubmit} disabled={submitting || disabled}>
						{submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{submitLabel}
					</Button>
					{onSubmitAndAddAnother && (
						<Button variant="outline" onClick={onSubmitAndAddAnother} disabled={submitting || disabled}>
							{submitLabel} and add another
						</Button>
					)}
					<Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
						Cancel
					</Button>
				</div>
			</SheetContent>
		</Sheet>
	);
}

/** Form field group used inside CrmCreateDrawer — a label + control pair stacked vertically. */
export function FormField({ label, required, children, hint }: { label: string; required?: boolean; children: ReactNode; hint?: string }) {
	return (
		<div className="space-y-1.5">
			<label className="text-sm font-medium text-foreground">
				{label} {required && <span className="text-destructive">*</span>}
			</label>
			{children}
			{hint && <p className="text-xs text-muted-foreground">{hint}</p>}
		</div>
	);
}
